import MultiStep from "components/shared/MultiStep";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IDeclaration } from "store/contributionStore";
import type { IUrssafErrors } from "store/paymentSlice";
import { fetchPaymentsBetweenDates, fetchURSSAFErrors } from "store/paymentSlice";
import { useAppDispatch, useAppSelector } from "store/store";

import { fetchCurrentCompanyGroup } from "../../../store/companyGroups/company-groups-use-cases";
import type { DeclarationsCardsData } from "../utils";
import { formatURSSAFErrors, getDeclarationPeriod } from "../utils";
import BeneficiariesTable from "./BeneficiariesTableStep";
import DownloadDeclarationStep from "./DownloadDeclarationStep";
import { getPaymentsInDeclaration, getReducedErrors } from "./utils";

interface IDeclarationModal {
    setModalType: Dispatch<SetStateAction<string>>;
    /**
     * Data of current declaration as set by getDeclarationCardsData (utils).
     */
    declarationData: IDeclaration | DeclarationsCardsData | undefined;
    /**
     * Closes modal.
     */
    handleClose: () => void;
}

/**
 * Within DeclarationPage (publisher/press), modal for checking beneficiaries info & download URSSAF file.
 */
const DeclarationModal = ({ setModalType, declarationData, handleClose }: IDeclarationModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { urssafErrors, urssafErrorsStatus, payments } = useAppSelector((state) => state.payment);

    const [activeStep, setActiveStep] = useState(0);

    const dateBoundaries = getDeclarationPeriod(declarationData?.year, declarationData?.quarter);

    const handleStep = {
        change: (_: any, idx: number) => setActiveStep(idx),
        next: () => setActiveStep((prev) => prev + 1),
        previous: () => setActiveStep((prev) => prev - 1),
    };

    /**
     * Payments that will actually be included in direction (grossValue > 0, etc.)
     */
    const beneficiaryPayments = getPaymentsInDeclaration({ payments, card: declarationData });

    /**
     * Returns all errors that belong to payments in declaration (aka filters those unpaid).
     */
    const usefulErrors = useMemo(() => {
        const errors: IUrssafErrors[] = [];
        urssafErrors.filter((error) => {
            if (error.line.computeFromRepresentative) {
                // If line causing error is computed from representative,
                // we use an includes as the id in the payment is structure as a concatenation of beneficiaryId and representativeId.
                const payment = beneficiaryPayments.find((item) =>
                    error.line.id.toString().includes(item.id.toString()),
                );
                payment && errors.push(error);
            } else {
                const payment = beneficiaryPayments.find((item) => item.id === error.line.id);
                payment && errors.push(error);
            }
        });
        return errors;
    }, [beneficiaryPayments, urssafErrors]);

    useEffect(() => {
        // Need to refetch payments to have correct data in table:
        dispatch(
            fetchPaymentsBetweenDates({
                startDate: new Date(dateBoundaries.startDate).toLocaleDateString("en-CA"),
                endDate: new Date(dateBoundaries.endDate).toLocaleDateString("en-CA"),
                forURSSAF: "true",
            }),
        );
        dispatch(
            fetchURSSAFErrors({
                startDate: new Date(dateBoundaries.startDate).toLocaleDateString("en-CA"),
                endDate: new Date(dateBoundaries.endDate).toLocaleDateString("en-CA"),
                downloadMode: false,
            }),
        );
        dispatch(fetchCurrentCompanyGroup());
    }, []);

    /**
     * Array with one line of errors per beneficiary.
     */
    const formattedErrors = formatURSSAFErrors(usefulErrors);

    const modalHeaderTitle =
        declarationData && declarationData?.quarter > 0
            ? t("Declarations.DeclareModal.DeclarationDateQuarter", {
                  quarter: declarationData?.quarter,
                  year: declarationData?.year,
              })
            : t("Declarations.DeclareModal.DeclarationDateYear", { year: declarationData?.year });

    return (
        <MultiStep
            title={modalHeaderTitle}
            activeStep={activeStep}
            onStepChange={handleStep.change}
            onClose={() => setModalType("")}
            animatedStepper
            style={{ height: "95%", minWidth: "1100px" }}
            steps={[
                {
                    title: t("Declarations.DeclareModal.Prepare"),
                    component: (
                        <BeneficiariesTable
                            data={beneficiaryPayments}
                            errors={formattedErrors}
                            errorsStatus={urssafErrorsStatus}
                            handleNextStep={handleStep.next}
                            declarationData={declarationData}
                            dateBoundaries={dateBoundaries}
                        />
                    ),
                },
                {
                    title: t("Declarations.DeclareModal.Declare"),
                    component: (
                        <DownloadDeclarationStep
                            handlePrevious={handleStep.previous}
                            declarationData={declarationData}
                            handleClose={handleClose}
                            allPayments={beneficiaryPayments}
                            errors={getReducedErrors(formattedErrors)}
                        />
                    ),
                },
            ]}
        />
    );
};

export default DeclarationModal;
